import { PersistentNotification } from '@reapit/elements'
import { Component, ErrorInfo, PropsWithChildren } from 'react'
// Adjust the import according to the actual export from the module
import { captureException } from '@reapit-sales/sentry-provider'
import { ErrorPagesProvider } from '../error-pages-handler'

/**
 * Interface for the state of the ErrorBoundary component.
 *
 * A boolean indicating whether an error has occurred within the component.
 */
export interface ErrorState {
  hasFailed: boolean
}

/**
 * A custom ErrorBoundary component that catches JavaScript errors in its subtree,
 * logs them to the console, reports them to Sentry, and renders a fallback UI.
 */
export class ErrorBoundary<T> extends Component<T & PropsWithChildren, ErrorState> {
  /**
   * The initial state of the ErrorBoundary, indicating no failure initially.
   */
  state = {
    hasFailed: false,
  }

  /**
   * Constructor for the ErrorBoundary component.
   * @param props The props passed to the component, including children.
   */
  constructor(props: T & PropsWithChildren) {
    super(props)
  }

  /**
   * Static lifecycle method invoked after an error has been thrown by a descendant component.
   * It should return a value to update the state.
   * @returns An object to update the state, in this case, setting `hasFailed` to true.
   */
  static getDerivedStateFromError() {
    return {
      hasFailed: true,
    }
  }

  /**
   * Lifecycle method invoked after an error has been caught by the ErrorBoundary.
   * It allows you to log the error and potentially perform side effects, like reporting to an error tracking service.
   * @param error The error object that was thrown.
   * @param info An object containing information about the component stack where the error occurred.
   */
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ERROR BOUNDARY CAUGHT', error.message, info)
    // ToDo React 19 Hook: Need to use the error hook once upgraded to React 19
    captureException(error, {
      extra: {
        componentStack: info.componentStack, // Provide component stack as extra context to Sentry
      },
    })
  }

  /**
   * The render method for the ErrorBoundary component.
   * It conditionally renders either the fallback UI if an error has occurred,
   * or its children if no error has been caught.
   * @returns A ReactNode representing either the fallback UI or the children.
   */
  render() {
    if (this.state.hasFailed) {
      return (
        <PersistentNotification isFullWidth isExpanded isInline intent="danger">
          Something went wrong here, try refreshing your page.
        </PersistentNotification>
      )
    }

    return <ErrorPagesProvider>{this.props.children}</ErrorPagesProvider>
  }
}
